import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { Politica } from '../../../api/ops/pagos';
import { ErrorApi } from '../../../api/problema';
import { Campo } from '../../../componentes/Campo';
import { Chip } from '../../../componentes/Chip';
import { Desplegable } from '../../../componentes/Desplegable';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { simboloMoneda } from '../../../logica/dinero';
import type { NegocioPagos } from '../negocio';
import { AYUDA_VARIOS_SERVICIOS, VARIOS_SERVICIOS, textoDeError } from '../textos';
import { condicionesDe, erroresDe, type Borrador } from './condiciones';

/**
 * Editor de una política (4.2): nueva (también «Duplicar», con las condiciones copiadas) o una versión nueva de una que
 * ya existe, con su nombre (renombrar, G279). «Así lo ve el cliente» se pide a la API mientras se escribe
 * (`previewPaymentPolicyTerms`, G278): una sola fuente del texto (D-004); sus errores de validación (p. ej. un cargo sin
 * tarjeta de garantía mayor que la señal, G280) se ven aquí antes de guardar.
 */
export type Edicion = { modo: 'nueva'; borrador: Borrador } | { modo: 'version'; politica: Politica; version: string | null; borrador: Borrador };

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    abierta: Edicion | null;
    n: NegocioPagos;
    onCerrar: () => void;
    onGuardar: (ed: Edicion, nombre: string) => Promise<void>;
}

const FORMAS = [
    { valor: 'per_person', texto: 'Por persona' },
    { valor: 'per_booking', texto: 'Por reserva' },
    { valor: 'per_unit', texto: 'Por unidad' },
    { valor: 'per_night', texto: 'Por noche (solo estancias)' },
] as const;

export function Editor({ ops, negocio, movil, abierta, n, onCerrar, onGuardar }: Props) {
    const [b, setB] = useState<Borrador | null>(abierta?.borrador ?? null);
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [vista, setVista] = useState<{ texto: string | null; error: string | null; cargando: boolean }>({ texto: null, error: null, cargando: false });
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const [ultima, setUltima] = useState<Edicion | null>(abierta);

    // Al abrir otro editor, se empieza desde su borrador.
    if (abierta !== ultima) {
        setUltima(abierta);
        setB(abierta?.borrador ?? null);
        setIntentado(false);
        setErrorApi(null);
        setVista({ texto: null, error: null, cargando: false });
    }

    const errores = b === null ? {} : erroresDe(b, true);
    const valido = Object.keys(errores).length === 0;
    const firma = b === null || !valido ? null : JSON.stringify(condicionesDe(b, n.moneda, n.citas));

    useEffect(() => {
        if (firma === null) {
            return;
        }
        let vivo = true;
        const reloj = setTimeout(() => {
            setVista((v) => ({ ...v, cargando: true }));
            ops.previewPaymentPolicyTerms(negocio, JSON.parse(firma) as Parameters<Operaciones['previewPaymentPolicyTerms']>[1])
                .then((r) => {
                    if (vivo) {
                        setVista({ texto: r.conditions_text, error: null, cargando: false });
                    }
                })
                .catch((e: unknown) => {
                    if (vivo) {
                        setVista({ texto: null, error: e instanceof ErrorApi && e.estado === 422 ? textoDeError(e, 'Revisa las condiciones.') : null, cargando: false });
                    }
                });
        }, 400);
        return () => {
            vivo = false;
            clearTimeout(reloj);
        };
    }, [ops, negocio, firma]);

    const cambiar = (c: Partial<Borrador>) => {
        setB((x) => (x === null ? x : { ...x, ...c }));
        setErrorApi(null);
    };
    const euro = simboloMoneda(n.moneda);
    const nueva = abierta?.modo === 'nueva';
    const siguiente = abierta?.modo === 'version' ? abierta.politica.current_version.number + 1 : 1;
    const ver = (k: keyof typeof errores) => (intentado ? errores[k] : undefined);

    const guardar = () => {
        setIntentado(true);
        if (abierta === null || b === null || !valido || vista.error !== null) {
            return;
        }
        setOcupado(true);
        onGuardar({ ...abierta, borrador: b }, b.nombre.trim())
            .catch((e: unknown) => {
                setErrorApi(textoDeError(e, 'No he podido guardar la política. Prueba de nuevo.'));
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <PanelLateral
            abierto={abierta !== null}
            onCerrar={onCerrar}
            movil={movil}
            titulo={nueva ? (b?.nombre.trim() || 'Nueva política') : (abierta?.politica.name ?? '')}
            eyebrow={nueva ? 'Nueva política de cobro' : `Versión ${String(siguiente)}`}
            subtitulo={nueva ? 'Se crea con su versión 1.' : `Ahora vale la versión ${String(siguiente - 1)}.`}
            icono={nueva ? 'plus' : 'pencil'}
            accion={nueva ? 'Crear política' : `Guardar versión ${String(siguiente)}`}
            accionIcono="check"
            ocupado={ocupado}
            onAccion={guardar}
            cerrar="Cancelar"
        >
            {b !== null && (
                <div className="rv-ca-cuerpo">
                    {errorApi !== null && (
                        <div className="rv-ca-caja rv-ca-caja--error" role="alert"><div className="rv-ca-caja__fila"><Icono nombre="circle-alert" /><span>{errorApi}</span></div></div>
                    )}
                    <Campo etiqueta="Nombre de la política" valor={b.nombre} onChange={(v) => { cambiar({ nombre: v }); }} max={120} error={ver('nombre')} ayuda="Solo lo ve tu equipo." />
                    <div className="rv-ca-bloque">
                        <p className="rv-ca-ceja">Cómo se calcula</p>
                        <Desplegable etiqueta="Forma de cálculo" valor={b.forma} opciones={FORMAS} onChange={(v) => { cambiar({ forma: v as Borrador['forma'] }); }} ayuda="Importes por persona. Por noche: solo estancias." />
                    </div>
                    <div className="rv-ca-bloque">
                        <p className="rv-ca-ceja">Al reservar</p>
                        <Interruptor etiqueta="Pedir tarjeta de garantía" ayuda="Stripe la guarda; al reservar no se cobra nada." icono="credit-card" valor={b.garantia} onChange={(v) => { cambiar({ garantia: v }); }} linea="arriba" />
                        <Campo etiqueta={`Señal (${euro})`} tipo="number" valor={b.senal} onChange={(v) => { cambiar({ senal: v }); }} error={ver('senal')} ayuda="0 = sin señal. Se devuelve si cancela a tiempo." />
                    </div>
                    <div className="rv-ca-bloque">
                        <p className="rv-ca-ceja">Si cancela tarde o no viene</p>
                        <Campo etiqueta={`Cargo por no-show (${euro})`} tipo="number" valor={b.noShow} onChange={(v) => { cambiar({ noShow: v }); }} error={ver('noShow')} ayuda="Solo cuando tu equipo marca el no-show." />
                        <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                            <Campo etiqueta={`Cancelación tardía (${euro})`} tipo="number" valor={b.tardia} onChange={(v) => { cambiar({ tardia: v }); }} error={ver('tardia')} />
                            <Campo etiqueta="Plazo (horas)" tipo="number" modo="numeric" valor={b.plazo} onChange={(v) => { cambiar({ plazo: v }); }} error={ver('plazo')} ayuda="Antes, cancelar es gratis." />
                        </div>
                        <span className="rv-ca-nota rv-ca-nota--gris">Los cargos descuentan la señal ya pagada.</span>
                    </div>
                    {n.citas && (
                        <div className="rv-ca-bloque">
                            <p className="rv-ca-ceja">Citas de varios servicios</p>
                            <Desplegable etiqueta="Señal de una cita con varios servicios" valor={b.varios} opciones={(['single_service', 'sum_all'] as const).map((v) => ({ valor: v, texto: VARIOS_SERVICIOS[v] }))} onChange={(v) => { cambiar({ varios: v as Borrador['varios'] }); }} />
                            {/* G433: con plazos de cancelación gratuita distintos vale el más largo; el texto del cliente sigue siendo `conditions_text`. */}
                            <span className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="clock" /><span>{AYUDA_VARIOS_SERVICIOS}</span></span>
                            <Chip estado="pending">Texto a validar</Chip>
                        </div>
                    )}
                    <Campo etiqueta="Nota para el cliente" valor={b.nota} onChange={(v) => { cambiar({ nota: v }); }} filas={3} max={500} ayuda="Se añade al final del texto; no lo sustituye." />
                    <div className="rv-ca-bloque">
                        <p className="rv-ca-ceja">Así lo ve el cliente</p>
                        {vista.error !== null ? (
                            <div className="rv-ca-caja rv-ca-caja--error" role="alert"><div className="rv-ca-caja__fila"><Icono nombre="circle-alert" /><span>{vista.error}</span></div></div>
                        ) : (
                            <div className="rv-pg-condiciones" aria-busy={vista.cargando}>{vista.texto ?? (valido ? 'Un momento…' : 'Revisa los importes para ver el texto.')}</div>
                        )}
                        <span className="rv-ca-nota rv-ca-nota--gris">El mismo texto en la web, el widget, el chat y la confirmación.</span>
                    </div>
                </div>
            )}
        </PanelLateral>
    );
}
